/**
 * Authentication Context
 * Manages customer authentication state, OTP login, and session persistence
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { cartService } from '../services/cartService';
import { useToast } from './ToastContext';
import AuthModal from '../components/feature/AuthModal';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { showToast, clearAllToasts } = useToast();

  const openAuthModal = useCallback(() => setIsAuthModalOpen(true), []);
  const closeAuthModal = useCallback(() => setIsAuthModalOpen(false), []);

  /**
   * Restore session from localStorage on mount
   */
  useEffect(() => {
    const token = localStorage.getItem('tcr_customer_token');
    const customerData = localStorage.getItem('tcr_customer_data');

    if (token && customerData) {
      try {
        const parsedCustomer = JSON.parse(customerData);
        setCustomer(parsedCustomer);
        setIsAuthenticated(true);
        console.log('🔐 Session restored:', parsedCustomer.fullName || parsedCustomer.mobileNumber);
      } catch (error) {
        console.error('❌ Failed to parse customer data:', error);
        // Clear corrupted data
        localStorage.removeItem('tcr_customer_token');
        localStorage.removeItem('tcr_customer_data');
      }
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setIsAuthenticated(false);
      setCustomer(null);
      setIsAuthModalOpen(true);
      showToast('Session expired. Please log in again.', 'error');
    };
    window.addEventListener('auth_unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth_unauthorized', handleUnauthorized);
  }, [showToast]);

  /**
   * Send OTP to mobile number
   */
  const sendOtp = useCallback(async (mobileNumber, method = 'sms') => {
    try {
      const response = await authService.sendOtp(mobileNumber, method);

      if (response.data.success) {
        showToast('OTP sent! Check your phone', 'success');
        return {
          success: true,
          message: response.data.message,
          data: response.data.data,
        };
      } else {
        showToast(response.data.message || 'Failed to send OTP', 'error');
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send OTP. Please try again.';
      showToast(errorMessage, 'error');
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, [showToast]);

  /**
   * Verify OTP and complete login/registration
   * @param {string} mobileNumber - Mobile number
   * @param {string} otp - OTP code
   * @param {Object} additionalData - Optional data for new users (fullName, email)
   */
  const verifyOtpAndLogin = useCallback(async (mobileNumber, otp, additionalData = {}) => {
    try {
      const requestData = {
        mobileNumber,
        otp,
        ...additionalData
      };

      const response = await authService.verifyOtp(requestData);

      if (response.data.success) {
        const { token, customer: customerData } = response.data.data;

        // Save to localStorage
        localStorage.setItem('tcr_customer_token', token);
        localStorage.setItem('tcr_customer_data', JSON.stringify(customerData));

        // Check for guest cart items and merge if necessary
        const guestCart = localStorage.getItem('tcr_cart');
        if (guestCart) {
          try {
            const items = JSON.parse(guestCart);
            if (items && items.length > 0) {
              console.log('🔄 Merging guest cart with user cart...');
              await cartService.mergeCart(items);
              localStorage.removeItem('tcr_cart'); // Clear after merge
              console.log('✅ Cart merged successfully');
            }
          } catch (err) {
            console.error('Failed to merge guest cart:', err);
            // We don't block login, but maybe show a toast warning?
            // For now, minimal disruption.
          }
        }

        // Update state
        setCustomer(customerData);
        setIsAuthenticated(true);

        clearAllToasts();
        showToast(`Welcome ${customerData.fullName || 'back'}!`, 'success');
        console.log('✅ Login successful:', customerData);

        return {
          success: true,
          message: 'Login successful',
          data: customerData,
        };
      } else {
        showToast(response.data.message || 'Invalid OTP', 'error');
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to verify OTP. Please try again.';
      showToast(errorMessage, 'error');
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, [showToast]);

  /**
   * Logout - clear session and redirect
   */
  const logout = useCallback(() => {
    localStorage.removeItem('tcr_customer_token');
    localStorage.removeItem('tcr_customer_data');
    setCustomer(null);
    setIsAuthenticated(false);
    showToast('Logged out successfully', 'success');
    console.log('👋 User logged out');
  }, [showToast]);

  /**
   * Update customer profile data (after profile completion)
   */
  const updateCustomerData = useCallback((updatedData) => {
    const updatedCustomer = { ...customer, ...updatedData };
    setCustomer(updatedCustomer);
    localStorage.setItem('tcr_customer_data', JSON.stringify(updatedCustomer));
    console.log('📝 Customer data updated:', updatedCustomer);
  }, [customer]);

  const value = {
    // State
    customer,
    isAuthenticated,
    isLoading,

    // Methods
    sendOtp,
    verifyOtpAndLogin,
    logout,
    updateCustomerData,
    isAuthModalOpen,
    openAuthModal,
    closeAuthModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
